import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import classNames from "classnames";
import dynamic from "next/dynamic";
import dayjs from "dayjs";
import { If } from "react-conditional-components-renderer";

import { reverseDateFormat } from "@ascendnow/shared";
import styles from "./AscDateInput.module.scss";
import AscDateInputAndOptionsWrapper from "./AscDateInputAndOptionsWrapper/AscDateInputAndOptionsWrapper";
import AscDatePickerPopover, {
  AscDatePickerPopoverValue,
} from "./AscDatePickerPopover/AscDatePickerPopover";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  formatDateToInputString,
  transformInputStringToDatePickerPopoverValue,
} from "./utilities/helpers";
import {
  ChevronDownIcon,
  CalendarEmptyIcon,
  CloseFilledIcon,
} from "../../AscIcons";
import { AscDateInputProps } from "./AscDateInput.types";
dayjs.extend(customParseFormat);

const ReactInputDateMask = dynamic(
  () => import("./AscDateInputMask/AscDateInputMask"),
  {
    ssr: false,
  }
);

const AscDateInput = function ({
  size = "medium",
  className,
  innerInputClassName,
  datepickerClassName,
  fullWidth = false,
  arrowIcon,
  showArrowIcon,
  dateIcon,
  onFocus,
  onChange,
  onBlur,
  onClear,
  range = false,
  children,
  disabled,
  placeholder,
  value,
  setFieldValue,
  format = "dd/mm/yyyy",
  isClear = false,
  ...rest
}: AscDateInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const toggleIconContainerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<string>();

  const datePickerValue: AscDatePickerPopoverValue = useMemo(
    () =>
      value && Array.isArray(value) && range
        ? {
            startDate: new Date(reverseDateFormat(value?.[0], "MM/DD/YYYY")),
            endDate: new Date(reverseDateFormat(value?.[1], "MM/DD/YYYY")),
          }
        : {
            startDate: value ? new Date(value as string) : null,
            endDate: null,
          },
    [value, range]
  );

  const setInputValueAndCallOnChange = useCallback(
    (stringValue: string) => {
      if (disabled) {
        return;
      }

      if (
        stringValue === value ||
        (Array.isArray(value) && stringValue === value?.join(" - "))
      )
        return;
      setInputValue(stringValue);
      onChange?.(
        (range ? stringValue?.split(" - ") : stringValue) as string &
          [string, string]
      );

      if (rest?.name) {
        setFieldValue?.(
          rest?.name,
          (range ? stringValue?.split(" - ") : stringValue) as string &
            [string, string]
        );
      }
    },
    [disabled, value, range, inputValue]
  );

  const setDatePickerValueFromValue = (value: string) => {
    const { value: _value, error } =
      transformInputStringToDatePickerPopoverValue(value, range);
    if (!error) {
      setInputValueAndCallOnChange(formatDateToInputString(_value));
    }
    setError(error);
  };

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    if (disabled) {
      return;
    }
    setIsOpen(true);
    onFocus?.(e);
  };

  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    // setDatePickerValueFromValue(e?.target?.value);
    onBlur?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setDatePickerValueFromValue(inputValue);
    } else if (range) {
      e.preventDefault();
    }
  };

  const handleOnChange = (value: string) => {
    setInputValueAndCallOnChange(value);
  };

  const open = () => {
    if (disabled) {
      return;
    }
    inputRef.current?.focus();
    setIsOpen(true);
  };

  const close = () => {
    inputRef.current?.blur();
    setIsOpen(false);
  };

  const clear = () => {
    setInputValueAndCallOnChange(
      formatDateToInputString({ startDate: null, endDate: null })
    );
    if (onClear) {
      onClear();
    }
  };

  // useEffect(() => {
  //   if (datePickerValue) {
  //     const stringValue = formatDateToInputString(datePickerValue);
  //     setInputValueAndCallOnChange(stringValue);
  //   }
  //   //eslint-disable-next-line
  // }, [datePickerValue]);

  useEffect(() => {
    if (!inputValue && typeof value === "string") {
      setInputValue(value);
      setDatePickerValueFromValue(value);
    }

    if (!value) {
      return;
    }

    if (range && typeof value === "string") {
      setInputValue(value);
      return;
    }

    if (range && Array.isArray(value)) {
      setInputValue(value?.join(" - "));
    }
  }, [value, range]);

  useEffect(() => {
    if (isClear) {
      setInputValue("");
      setInputValueAndCallOnChange(
        formatDateToInputString({ startDate: null, endDate: null })
      );
    }
  }, [isClear, setInputValueAndCallOnChange]);

  const suffixIcon = arrowIcon ? arrowIcon : <ChevronDownIcon />;

  return (
    <AscDateInputAndOptionsWrapper
      content={
        <AscDatePickerPopover
          value={datePickerValue}
          onChange={(val) => {
            setInputValueAndCallOnChange(formatDateToInputString(val));
            if (range && val && val.startDate && val.endDate) {
              setIsOpen(false);
            } else if (!range && val && val.startDate) {
              setIsOpen(false);
            }
          }}
          range={range}
        />
      }
      visible={isOpen && !disabled}
      contentClassName={datepickerClassName}
      className={classNames(
        styles.ground,
        {
          [styles.large]: size === "large",
          [styles.medium]: size === "medium",
          [styles.error]: !!error,
          [styles.disabled]: disabled,
          [styles.fullWidth]: fullWidth,
          [styles.isOpen]: isOpen,
        },
        className
      )}
      onClickOutsideContainer={close}
      toggleIconContainerRef={toggleIconContainerRef}
    >
      <If test={dateIcon !== null}>
        <span className={styles.prefixIcon}>
          {dateIcon ? dateIcon : <CalendarEmptyIcon />}
        </span>
      </If>

      <div className={styles.inputWrapper}>
        {range ? (
          <input
            ref={inputRef}
            className={classNames(styles.innerInput, innerInputClassName)}
            onFocus={handleOnFocus}
            onChange={(e) => handleOnChange(e?.target?.value)}
            onBlur={handleOnBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            value={inputValue}
            placeholder={
              placeholder || (range ? "DD/MM/YYYY - DD/MM/YYYY" : "DD/MM/YYYY")
            }
            {...rest}
          />
        ) : (
          <ReactInputDateMask
            inputRef={inputRef}
            className={classNames(styles.innerInput, innerInputClassName)}
            onFocus={handleOnFocus}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            value={inputValue}
            mask={format}
            placeholder={
              placeholder || (range ? "DD/MM/YYYY - DD/MM/YYYY" : "DD/MM/YYYY")
            }
            {...rest}
          />
        )}
      </div>

      <div ref={toggleIconContainerRef}>
        <If
          test={
            showArrowIcon && suffixIcon !== null && !datePickerValue?.startDate
          }
        >
          <span
            className={classNames(styles.suffixIcon, styles.focusIcon)}
            onClick={open}
          >
            {suffixIcon}
          </span>
          <span
            className={classNames(styles.suffixIcon, styles.blurIcon)}
            onClick={close}
          >
            {suffixIcon}
          </span>
        </If>
        <If
          test={
            (!!datePickerValue && datePickerValue?.startDate !== null) ||
            !!value
          }
        >
          <span className={classNames(styles.suffixIcon)} onClick={clear}>
            <CloseFilledIcon />
          </span>
        </If>
      </div>
    </AscDateInputAndOptionsWrapper>
  );
};

export default AscDateInput;
